import React, { useMemo } from 'react';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { UserType } from 'src/graphql';
import { useSearchUsers } from 'src/hooks';
import {
  evidenceS3SourceFromQaFileKey,
  parseDynamicQuestionUploadedFilesFromAnswerText,
} from 'src/utils/dynamicQuestionUploadedFiles';
import { DateFormats, formatDate, parseTimeShortText } from 'src/utils/time';
import {
  ReadOnlyDataGridTable,
  ReadOnlyDataGridTd,
  ReadOnlyDataGridTh,
  ReadOnlyFileList,
  ReadOnlyFileListItem,
  ReadOnlyFileName,
  ReadOnlyValue,
  ReadOnlyValueWithIcon,
} from './DynamicQuestion.styles';
import { DynamicQuestionFileDownloadList } from './DynamicQuestionFileDownloadList';
import {
  COI_EMPLOYEE_DIRECTORY_SUBTYPE_KEYS,
  DATABASE_EMPLOYEE_DIRECTORY_FIELDS,
} from './DynamicQuestionProps';
import type {
  DynamicQuestionAnswerValue,
  DynamicQuestionData,
  DynamicQuestionOption,
} from './types';
import { QuestionTypeTypeKey } from './types';

type DropdownOption = { value: string; label: string };

export interface DynamicQuestionReadOnlyValueProps {
  typeKey: QuestionTypeTypeKey | string;
  value: DynamicQuestionAnswerValue;
  question: DynamicQuestionData;
  baseTestId: string;
  dropdownOptions: DropdownOption[];
  options: DynamicQuestionOption[];
  reportIdForFileDownload?: string | null;
  fileDownloadCaseManagerView: boolean;
}

export const DynamicQuestionReadOnlyValue: React.FC<
  DynamicQuestionReadOnlyValueProps
> = ({
  typeKey,
  value,
  question,
  baseTestId,
  dropdownOptions,
  options,
  reportIdForFileDownload,
  fileDownloadCaseManagerView,
}) => {
  type DatabaseFieldOption = { label: string; value: string; user: UserType };

  const subTypeKey = question.databaseFieldSubType?.subTypeKey;
  const isEmployeeDirectoryField =
    typeKey === QuestionTypeTypeKey.DATABASE_FIELD &&
    DATABASE_EMPLOYEE_DIRECTORY_FIELDS.includes(subTypeKey || '');
  const numericIds = isEmployeeDirectoryField
    ? (value.textValues ?? []).filter(
        (v) => v != null && /^\d+$/.test(v.trim()),
      )
    : [];
  const shouldLookupUsers = isEmployeeDirectoryField && numericIds.length > 0;
  const userIdsForDatabaseField = shouldLookupUsers ? numericIds : undefined;
  const { sortedValidUsers } = useSearchUsers(
    [],
    [],
    false,
    false,
    null,
    undefined,
    undefined,
    userIdsForDatabaseField,
    shouldLookupUsers,
  );

  const databaseFieldOptions = useMemo<DatabaseFieldOption[]>(() => {
    if (DATABASE_EMPLOYEE_DIRECTORY_FIELDS.includes(subTypeKey || '')) {
      return (sortedValidUsers || []).map((user) => ({
        label: user.name || 'Anonymous',
        value: String(user.id),
        user: user as UserType,
      }));
    }
    return [];
  }, [sortedValidUsers, subTypeKey]);

  if (value.isSanitized) {
    return (
      <ReadOnlyValue data-testid={`${baseTestId}-readonly-value`}>
        [SANITIZED]
      </ReadOnlyValue>
    );
  }

  switch (typeKey) {
    case QuestionTypeTypeKey.SHORT_TEXT:
    case QuestionTypeTypeKey.NUMBER:
      return (
        <ReadOnlyValue data-testid={`${baseTestId}-readonly-value`}>
          {value.text ?? '—'}
        </ReadOnlyValue>
      );
    case QuestionTypeTypeKey.LONG_TEXT:
      return (
        <ReadOnlyValue data-testid={`${baseTestId}-readonly-value`}>
          {value.text ?? '—'}
        </ReadOnlyValue>
      );
    case QuestionTypeTypeKey.DATE_TIME:
      if (value.dateTimeUnsure) {
        return (
          <ReadOnlyValue data-testid={`${baseTestId}-readonly-value`}>
            I am not sure
          </ReadOnlyValue>
        );
      }
      return (
        <ReadOnlyValueWithIcon data-testid={`${baseTestId}-readonly-value`}>
          <i
            className="uil uil-calendar-alt"
            aria-hidden
            style={{ fontSize: '1rem' }}
          />
          {value.text ?? '—'}
        </ReadOnlyValueWithIcon>
      );
    case QuestionTypeTypeKey.TIME: {
      const parsed = parseTimeShortText(value.text);
      const display = parsed
        ? formatDate(parsed, DateFormats.HOUR_MINUTE_AMPM, false)
        : '';
      return (
        <ReadOnlyValueWithIcon data-testid={`${baseTestId}-readonly-value`}>
          <i
            className="uil uil-clock-five"
            aria-hidden
            style={{ fontSize: '1rem' }}
          />
          {display || '—'}
        </ReadOnlyValueWithIcon>
      );
    }
    case QuestionTypeTypeKey.DROPDOWN:
    case QuestionTypeTypeKey.RADIO: {
      const selected = dropdownOptions.find(
        (o) => o.value === value.selectedOptionId,
      );
      return (
        <ReadOnlyValue data-testid={`${baseTestId}-readonly-value`}>
          {selected?.label ?? '—'}
        </ReadOnlyValue>
      );
    }
    case QuestionTypeTypeKey.CHECKBOX:
    case QuestionTypeTypeKey.MULTIPLE_CHOICE: {
      const selectedIds = value.selectedOptionIds ?? [];
      const labels = selectedIds
        .map((id) => options.find((o) => o.id === id)?.optionText)
        .filter(Boolean)
        .join(', ');
      return (
        <ReadOnlyValue data-testid={`${baseTestId}-readonly-value`}>
          {labels || '—'}
        </ReadOnlyValue>
      );
    }
    case QuestionTypeTypeKey.DATA_GRID: {
      const dataGridConfig = question.dataGridConfig;
      const columns = dataGridConfig?.columns ?? [];
      const rows = value.dataGridRows ?? [];
      if (columns.length === 0) {
        return (
          <ReadOnlyValue data-testid={`${baseTestId}-readonly-value`}>
            —
          </ReadOnlyValue>
        );
      }
      return (
        <ReadOnlyDataGridTable data-testid={`${baseTestId}-readonly-value`}>
          <thead>
            <tr>
              {columns.map((col) => (
                <ReadOnlyDataGridTh key={col.key}>
                  {col.label}
                </ReadOnlyDataGridTh>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {columns.map((col) => (
                  <ReadOnlyDataGridTd key={col.key}>
                    {String(row[col.key] ?? '')}
                  </ReadOnlyDataGridTd>
                ))}
              </tr>
            ))}
          </tbody>
        </ReadOnlyDataGridTable>
      );
    }
    case QuestionTypeTypeKey.DATABASE_FIELD: {
      const providedValues = value.textValues ?? [];
      const icon = isEmployeeDirectoryField ? 'uil uil-user' : null;

      const resolvedEntries = providedValues.map((rawValue) => {
        if (rawValue != null && /^\d+$/.test(rawValue.trim())) {
          const matched = databaseFieldOptions.find(
            (o) => o.value === String(rawValue),
          );
          if (matched) {
            const detailFields = COI_EMPLOYEE_DIRECTORY_SUBTYPE_KEYS.includes(
              subTypeKey || '',
            )
              ? [
                  matched.user?.role,
                  matched.user?.department,
                  matched.user?.country,
                ]
              : [matched.user?.department, matched.user?.site];
            const parts = detailFields.filter(Boolean).join(', ');
            return { name: matched.label, detail: parts || undefined };
          }
          return { name: rawValue };
        }
        return { name: rawValue ?? '—' };
      });

      if (resolvedEntries.length === 0) {
        return (
          <ReadOnlyValue data-testid={`${baseTestId}-readonly-value`}>
            —
          </ReadOnlyValue>
        );
      }

      return (
        <ReadOnlyValue data-testid={`${baseTestId}-readonly-value`}>
          {resolvedEntries.map((entry, i) => (
            <ReadOnlyValueWithIcon key={i}>
              {icon && (
                <StyledIcon
                  className={icon}
                  size="small"
                  color="theme.newPalette.neutral.N800"
                />
              )}
              {entry.name}
              {entry.detail && ` — ${entry.detail}`}
            </ReadOnlyValueWithIcon>
          ))}
        </ReadOnlyValue>
      );
    }
    case QuestionTypeTypeKey.FILE_UPLOAD: {
      const parsedFromText = parseDynamicQuestionUploadedFilesFromAnswerText(
        value.text,
      );
      const fromState = value.uploadedFiles ?? [];
      const fromEvidence = (value.evidenceUploadFiles ?? []).map((f) => ({
        fileName: f.options.file.name,
        s3Path: f.source,
      }));
      const fromQaAttachments = (value.qaAnswerAttachments ?? [])
        .map((att) => ({
          fileName: att.fileName ?? 'attachment',
          s3Path: evidenceS3SourceFromQaFileKey(
            att.fileKey,
            reportIdForFileDownload ?? '',
          ),
        }))
        .filter((entry) => entry.s3Path !== '');
      const downloadFiles =
        fromState.length > 0
          ? fromState
          : parsedFromText.length > 0
            ? parsedFromText
            : fromEvidence.length > 0
              ? fromEvidence
              : fromQaAttachments;
      if (downloadFiles.length === 0) {
        return (
          <ReadOnlyValue data-testid={`${baseTestId}-readonly-value`}>
            —
          </ReadOnlyValue>
        );
      }
      const canDownload =
        reportIdForFileDownload != null &&
        reportIdForFileDownload !== '' &&
        downloadFiles.every((f) => f.s3Path.trim() !== '');
      if (canDownload) {
        return (
          <DynamicQuestionFileDownloadList
            files={downloadFiles}
            reportId={reportIdForFileDownload}
            caseManagerView={fileDownloadCaseManagerView}
            data-testid={`${baseTestId}-readonly-value`}
          />
        );
      }
      return (
        <ReadOnlyFileList data-testid={`${baseTestId}-readonly-value`}>
          {downloadFiles.map((file, i) => (
            <ReadOnlyFileListItem key={`${file.fileName}-${i}`}>
              <i
                className="uil uil-file-alt"
                aria-hidden
                style={{ fontSize: '1rem' }}
              />
              <ReadOnlyFileName title={file.fileName}>
                {file.fileName}
              </ReadOnlyFileName>
            </ReadOnlyFileListItem>
          ))}
        </ReadOnlyFileList>
      );
    }
    default:
      return (
        <ReadOnlyValue data-testid={`${baseTestId}-readonly-value`}>
          {value.text ?? '—'}
        </ReadOnlyValue>
      );
  }
};
